import * as Sentry from "@sentry/nextjs";
import { GraphQLErrors, NetworkError } from "@apollo/client/errors";
import { ExecutionResult } from "graphql";
import { ChangesetErrorKey } from "../types";

export const captureInSentry = (e: unknown, operationName?: string) => {
  Sentry.captureException(e, { tags: { operationName } });
  return;
};

/**
 * Logs a network error to the console and Sentry.
 *
 * Note: DataDog captures errors automatically, so we only need to log them here.
 *
 * @param e The error to log.
 * @param operationName The name of the operation that triggered the error.
 */
export const logServerNetworkError = (
  e: NetworkError,
  operationName?: string
) => {
  // https://github.com/pepsico-ecommerce/pepdirect/pull/1040
  const stackTrace = e?.stack
    ? e.stack
        .split("\n")
        .map((str) => str.trim())
        .join(" ")
    : "";
  e &&
    console.error(
      JSON.stringify({ message: e.message, stack: e.stack, name: stackTrace })
    );
  captureInSentry(e, operationName);
};

export const logClientSideError = (e: unknown, operationName?: string) => {
  console.error(e);
  captureInSentry(e, operationName);
};

export const logUnknownErrorCode = (
  code: string,
  operationName: string,
  // using any here in case the errors are malformed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorDetail: any
) => {
  const errorMsg =
    code === ChangesetErrorKey
      ? `Unhandled changeset error from ${operationName} GQL operation. All ` +
        `new errors must be mapped to the frontend. Please report to backend ` +
        `team for triage. Error details:\n${JSON.stringify(
          errorDetail,
          null,
          2
        )}`
      : `Unknown error "${code}" from ${operationName} GQL operation. All ` +
        `new errors must be mapped to the frontend. Please report to backend ` +
        `team for triage.`;

  console.warn(errorMsg);
  Sentry.captureMessage(errorMsg);
};

export const logWithMessageInSentry = (msg: string) => {
  console.error(msg);
  Sentry.captureMessage(msg);
};

interface LogNonArrayGraphQLErrors {
  graphQLErrors: GraphQLErrors;
  operationName: string;
  response: ExecutionResult | undefined;
}

// graphQLErrors should be an array, log it when we don't get it as
// one for further investigation.
export const logNonArrayGraphQLErrors = ({
  graphQLErrors,
  operationName,
  response,
}: LogNonArrayGraphQLErrors) => {
  const value =
    typeof graphQLErrors === "object"
      ? JSON.stringify(graphQLErrors, null, 2)
      : graphQLErrors;

  Sentry.captureMessage(
    `graphQLErrors should be an array on ${operationName}
      and is being returned as type: ${typeof graphQLErrors},
      value: ${value}, and response: ${JSON.stringify(response, null, 2)}
    `,
    "warning"
  );
};
